import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { ThemeService } from '@caslink/common-client/ng-app/theme/ThemeService';
import { CommandService } from '../../shared/services/command.service';
import { NumberSensorInputMap } from '../../shared/models/device';
import { DeviceInputBaseComponent } from '../device-input-base.component';
import {
    CommandOptimizationRenderingService
} from '@caslink/device-client/ng-app/shared/services/command-optimization-rendering.service';

@Component({
    selector: 'dl-number-sensor',
    templateUrl: './number-sensor-input.component.html',
    styleUrls: ['../validation.scss', './number-sensor-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberSensorInputComponent extends DeviceInputBaseComponent {
    public static $name: string = 'numberSensor';

    public hasMinMax: boolean = false;

    public step: string;
    public ngStep: string;
    public units: string;

    formControl: FormControl;

    alertClass;

    constructor(
        private commandService: CommandService,
        private themeService: ThemeService,
        protected commandOptimizationRenderingService: CommandOptimizationRenderingService,
        protected cdr: ChangeDetectorRef,
    ) {
        super(commandService, commandOptimizationRenderingService, cdr);
    }

    public ngOnInit() {
        this.alertClass = this.themeService.getTextAccentClass();

        super.ngOnInit();

        const numberSensorMap = this.deviceInput.$inputTypeMap as NumberSensorInputMap;

        if (numberSensorMap) {
            this.units = numberSensorMap.units;
            this.step = numberSensorMap.step;
            this.ngStep = numberSensorMap.ngStep;
        }

        this.initFormMetadata();
    }

    public determineHasMinMax() {
        this.hasMinMax = this.hasMinValue() && this.hasMaxValue();
    }

    public initFormMetadata() {
        this.formControl = this.deviceInputForm?.get(this.deviceInput.$fieldName) as FormControl || new FormControl(this.deviceInput.value, this.getValidators());

        this.determineHasMinMax();

        this.deviceInputForm.addControl(this.deviceInput.$fieldName, this.formControl);
    }

    // This and getValidators must be arrow functions so that their scope won't be lost when passed to child components
    invokeCommand = () => {
        this.deviceInput.value = this.formControl.value;
        this.invokeChangeCommand(this.deviceInput);
    }

    getValidators = () => {
        let validators: ValidatorFn[] = [];
        /** Don't apply validation to disabled inputs -- the user won't be able to edit them to fix, therefore won't be able to save */
        if (this.deviceInput.isEnabled) {
            validators.push(Validators.required);

            if (this.hasMinValue()) {
                validators.push(Validators.min(this.deviceInput.minValue));
            }

            if (this.hasMaxValue()) {
                validators.push(Validators.max(this.deviceInput.maxValue));
            }

            if (this.ngStep) {
                validators.push(this.ngStepValidatorFactory());
            }
        }
        return validators;
    }

    // '0' is a valid value for min & the 'in' operator does not work in a template expression
    private hasMinValue() {
        return 'minValue' in this.deviceInput;
    }

    // '0' is a valid value for max & the 'in' operator does not work in a template expression
    private hasMaxValue() {
        return 'maxValue' in this.deviceInput;
    }

    // copied/adapted from the source code of AngularJS
    private ngStepValidatorFactory() {
        return (control: FormControl) => {
            function isNumberInteger(num) {
                // See http://stackoverflow.com/questions/14636536/how-to-check-if-a-variable-is-an-integer-in-javascript#14794066
                // (minus the assumption that `num` is a number)

                // tslint:disable-next-line no-bitwise
                return (num | 0) === num;
            }

            function countDecimals(num) {
                if (!num) {
                    return 0;
                }

                var numString = num.toString();
                var decimalSymbolIndex = numString.indexOf('.');

                if (decimalSymbolIndex === -1) {
                    if (-1 < num && num < 1) {
                        // It may be in the exponential notation format (`1e-X`)
                        var match = /e-(\d+)$/.exec(numString);

                        if (match) {
                            return Number(match[1]);
                        }
                    }

                    return 0;
                }

                return numString.length - decimalSymbolIndex - 1;
            }

            let value = control.value;
            let stepBase = this.deviceInput.minValue || 0;
            let step = parseFloat(this.ngStep);

            var isNonIntegerValue = !isNumberInteger(value);
            var isNonIntegerStepBase = !isNumberInteger(stepBase);
            var isNonIntegerStep = !isNumberInteger(step);

            // Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
            // `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
            if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
                var valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
                var stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
                var stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

                var decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
                // tslint:disable-next-line: no-magic-numbers
                var multiplier = Math.pow(10, decimalCount);

                value = value * multiplier;
                stepBase = stepBase * multiplier;
                step = step * multiplier;

                if (isNonIntegerValue) { value = Math.round(value); }
                if (isNonIntegerStepBase) { stepBase = Math.round(stepBase); }
                if (isNonIntegerStep) { step = Math.round(step); }
            }

            return (value - stepBase) % step === 0 ? null : { ngStep: control.value };
        };
    }
}
